import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PaymentButton from "../components/PaymentButton";
import { sendMembershipActivatedEmail } from "../services/emailService";

const STATES = ["Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno","Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","Gombe","Imo","Jigawa","Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara","Lagos","Nasarawa","Niger","Ogun","Ondo","Osun","Oyo","Plateau","Rivers","Sokoto","Taraba","Yobe","Zamfara"];

const BENEFITS = [
  { icon: "📋", title: "Grant Access", desc: "Unlock all grant programs exclusively for registered members." },
  { icon: "📊", title: "Member Dashboard", desc: "Track applications, receive updates, manage your profile in real time." },
  { icon: "🔔", title: "Priority Notifications", desc: "First to know about new grant cycles, deadlines, and opportunities." },
  { icon: "🧑‍🤝‍🧑", title: "Community Network", desc: "Connect with 18,400+ NGO leaders and social entrepreneurs." },
  { icon: "📚", title: "Resource Library", desc: "Access grant writing guides, templates, and capacity building materials." },
  { icon: "🎓", title: "Training & Webinars", desc: "Free monthly webinars on nonprofit management and fundraising." },
];

const FAQS = [
  { q: "Who can become a member?", a: "Any individual, NGO, community group, or social enterprise operating in Nigeria." },
  { q: "Is the ₦250 a one-time fee?", a: "Yes. The ₦250 membership fee is a one-time contribution covering lifetime access. No renewals, no hidden costs." },
  { q: "When can I apply for a grant after paying?", a: "Immediately! Your membership is activated instantly after successful payment via Korapay." },
  { q: "What payment methods are accepted?", a: "We accept all Nigerian bank cards (Visa, Mastercard, Verve), bank transfers, and USSD payments via Korapay." },
  { q: "Is my payment secure?", a: "Yes. All payments are processed by Korapay, Nigeria's leading payment gateway, with bank-level SSL encryption." },
  { q: "What if my registration fails?", a: "Ensure your email is valid, password is at least 8 characters with one uppercase letter and one number, and both passwords match. Contact support if issues persist." },
];

const getRegisterError = (code) => {
  switch (code) {
    case "auth/email-already-in-use": return "An account with this email already exists. Please sign in instead.";
    case "auth/invalid-email":        return "Please enter a valid email address.";
    case "auth/weak-password":        return "Password must be at least 8 characters long, include at least one uppercase letter and one number.";
    case "auth/network-request-failed": return "Network error. Please check your internet connection.";
    case "auth/too-many-requests":    return "Too many attempts. Please wait a few minutes and try again.";
    default: return "Registration failed. Please check your details and try again.";
  }
};

export default function MembershipPage() {
  const { register, currentUser, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]       = useState(currentUser && !userProfile?.hasPaidMembership ? "payment" : "form"); // form | payment | success
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  // Store user details separately so they survive auth state re-renders
  const [payEmail, setPayEmail]   = useState("");
  const [payUid, setPayUid]       = useState("");
  const [payName, setPayName]     = useState("");
  const [memberId, setMemberId]   = useState("");

  useEffect(() => {
    if (currentUser && userProfile && !userProfile.hasPaidMembership) {
      setPayEmail(currentUser.email);
      setPayUid(currentUser.uid);
      setPayName(userProfile.name);
      setMemberId(userProfile.memberId);
    }
  }, [currentUser, userProfile]);

  const [form, setForm] = useState({
    name: "", email: "", password: "", confirmPassword: "",
    phone: "", org: "", orgType: "", state: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim())                      return "Full name is required.";
    if (!form.email.trim())                     return "Email address is required.";
    if (!form.email.includes("@"))             return "Please enter a valid email address.";
    if (!form.password)                         return "Password is required.";
    if (form.password.length < 8)               return "Password must be at least 8 characters.";
    if (!/[A-Z]/.test(form.password))           return "Password must contain at least one uppercase letter.";
    if (!/[0-9]/.test(form.password))           return "Password must contain at least one number.";
    if (!form.confirmPassword)                  return "Please confirm your password.";
    if (form.password !== form.confirmPassword) return "Passwords do not match.";
    if (!form.state)                            return "Please select your state of operation.";
    return null;
  };

  const handleRegister = async () => {
    setError("");
    const err = validate();
    if (err) { setError(err); return; }
    try {
      setLoading(true);
      const result = await register(form.email.trim(), form.password, {
        name: form.name.trim(), phone: form.phone.trim(),
        org: form.org.trim(), orgType: form.orgType, state: form.state,
      });
      const { user, memberId: mid } = result;
      // Save payment details to state BEFORE changing step
      // This prevents the Korapay button from losing context
      setPayEmail(form.email.trim());
      setPayUid(user.uid);
      setPayName(form.name.trim());
      setMemberId(mid);
      // Now move to payment step
      setStep("payment");
    } catch (err) {
      setError(getRegisterError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (response) => {
    await refreshProfile();
    // Send membership activated email (non-blocking)
    sendMembershipActivatedEmail({
      name:       payName,
      email:      payEmail,
      memberId:   memberId,
      paymentRef: response?.transaction_id?.toString() || response?.reference || "",
    }).catch(err => console.warn("Membership email failed (non-critical):", err));
    setStep("success");
  };

  // Only show "already member" screen if they are paid AND we are NOT in an active payment flow
  const alreadyPaid = currentUser && userProfile?.hasPaidMembership && step === "form";

  if (alreadyPaid) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--off-white)", padding:"120px 5vw 80px", textAlign:"center" }}>
        <div>
          <div style={{ fontSize:64, marginBottom:16 }}>✅</div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:32, color:"var(--navy)", marginBottom:12 }}>You're Already a Member!</h2>
          <p style={{ color:"var(--text-muted)", fontSize:16, lineHeight:1.75, maxWidth:400, margin:"0 auto 28px" }}>
            Your membership is active. You can apply for grants immediately.
          </p>
          <Link to="/apply" className="btn-primary" style={{ display:"inline-block", marginRight:12 }}>Apply for Grant →</Link>
          <Link to="/dashboard" className="btn-outline-dark" style={{ display:"inline-block" }}>Go to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .mem-hero { background:linear-gradient(160deg,#071428,#0B1F3A,#122844); padding:140px 5vw 80px; text-align:center; }
        .mem-hero h1 { font-family:'Playfair Display',serif; font-size:clamp(36px,6vw,68px); font-weight:900; color:white; margin-bottom:18px; }
        .benefits-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:22px; max-width:1100px; margin:48px auto 0; }
        .benefit-card { padding:28px 24px; border:1px solid var(--border-light); background:white; transition:all 0.3s; }
        .benefit-card:hover { transform:translateY(-3px); box-shadow:var(--shadow-md); }
        .reg-layout { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:flex-start; max-width:1100px; margin:0 auto; }
        .reg-card { background:white; border:1px solid var(--border-light); padding:36px; }
        .form-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        .faq-item { border-bottom:1px solid var(--border-light); }
        .faq-q { display:flex; justify-content:space-between; align-items:center; padding:16px 0; cursor:pointer; font-weight:600; color:var(--navy); font-size:14px; gap:10px; }
        .faq-a { font-size:13px; color:var(--text-muted); line-height:1.7; padding-bottom:16px; }
        .required { color:var(--gold); }
        .payment-card { background:white; border:1px solid var(--border-light); padding:36px; text-align:center; }
        .step-progress { display:flex; gap:0; margin-bottom:28px; }
        .prog-item { flex:1; text-align:center; }
        .prog-circle { width:32px; height:32px; border-radius:50%; margin:0 auto 8px; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; }
        .prog-label { font-size:11px; }
        @media(max-width:900px){ .benefits-grid{grid-template-columns:repeat(2,1fr);} .reg-layout{grid-template-columns:1fr;} .form-row{grid-template-columns:1fr;} }
        @media(max-width:560px){ .benefits-grid{grid-template-columns:1fr;} }
      `}</style>

      {/* HERO */}
      <section className="mem-hero">
        <span className="section-eyebrow" style={{ color:"#E8B96A" }}>Membership</span>
        <h1>Join the<br /><em style={{ fontStyle:"italic", color:"#E8B96A" }}>Revolutionary</em><br />Community</h1>
        <p style={{ color:"rgba(255,255,255,0.6)", fontSize:17, maxWidth:500, margin:"16px auto 0", lineHeight:1.75 }}>
          18,400+ members. One purpose. Unlocking access to grants that transform communities.
        </p>
        <div style={{ display:"inline-flex", alignItems:"center", gap:12, background:"rgba(201,150,62,0.1)", border:"1px solid rgba(201,150,62,0.3)", padding:"12px 28px", borderRadius:4, margin:"24px auto 0" }}>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:40, fontWeight:900, color:"#E8B96A" }}>₦250</div>
          <div>
            <strong style={{ color:"white", fontSize:14, display:"block" }}>One-Time Membership Fee</strong>
            <span style={{ color:"rgba(255,255,255,0.45)", fontSize:12 }}>Lifetime access · No renewal · Instant activation</span>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section style={{ padding:"80px 5vw", background:"var(--off-white)" }}>
        <div style={{ textAlign:"center" }}>
          <span className="section-eyebrow">Why Join</span>
          <h2 className="section-title">Member Benefits</h2>
        </div>
        <div className="benefits-grid">
          {BENEFITS.map(b => (
            <div className="benefit-card" key={b.title}>
              <div style={{ fontSize:28, marginBottom:12 }}>{b.icon}</div>
              <div className="gold-line" />
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, color:"var(--navy)", marginBottom:8 }}>{b.title}</div>
              <div style={{ fontSize:14, color:"var(--text-muted)", lineHeight:1.7 }}>{b.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* REGISTRATION + PAYMENT */}
      <section style={{ padding:"80px 5vw", background:"white" }}>
        <div className="reg-layout">

          {/* LEFT */}
          <div>
            <span className="section-eyebrow">
              {step === "form" ? "Step 1 of 2 — Registration" : step === "payment" ? "Step 2 of 2 — Payment" : "Complete!"}
            </span>
            <h2 className="section-title">
              {step === "form" ? "Create Your Account" : step === "payment" ? "Activate Membership" : "You're All Set!"}
            </h2>
            <p style={{ color:"var(--text-muted)", fontSize:15, lineHeight:1.8, marginBottom:24 }}>
              {step === "form"
                ? "Fill out the form to create your account. You'll then pay the ₦250 membership fee via Korapay to get instant access."
                : step === "payment"
                ? "Your account is created! Complete the ₦250 payment below to unlock all grant programs immediately."
                : "Your membership is now active. You can apply for grants right away!"
              }
            </p>

            {/* PROGRESS */}
            <div className="step-progress">
              {["Create Account","Pay ₦250","Get Access"].map((label, i) => {
                const isDone   = (step === "payment" && i === 0) || (step === "success" && i <= 1);
                const isActive = (step === "form" && i === 0) || (step === "payment" && i === 1) || (step === "success" && i === 2);
                return (
                  <div className="prog-item" key={label}>
                    <div className="prog-circle" style={{ background: isDone ? "#16a34a" : isActive ? "var(--navy)" : "var(--border-light)", color: (isDone || isActive) ? "white" : "var(--text-muted)" }}>
                      {isDone ? "✓" : i + 1}
                    </div>
                    <div className="prog-label" style={{ color: isActive ? "var(--navy)" : "var(--text-muted)", fontWeight: isActive ? 600 : 400 }}>{label}</div>
                  </div>
                );
              })}
            </div>

            {step === "payment" && (
              <div style={{ background:"rgba(201,150,62,0.04)", border:"1px solid rgba(201,150,62,0.15)", padding:"20px", borderRadius:4, marginTop:8 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"var(--navy)", marginBottom:12 }}>✅ What you unlock after payment:</div>
                {["Instant access to all grant programs","Member dashboard with live tracking","Priority notifications for grant cycles","Resource library & free webinars"].map(item => (
                  <div key={item} style={{ fontSize:13, color:"var(--text-muted)", padding:"4px 0", display:"flex", gap:8 }}>
                    <span style={{ color:"#16a34a", flexShrink:0 }}>✓</span> {item}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT */}
          <div>
            {/* ── STEP 1: REGISTRATION FORM ── */}
            {step === "form" && (
              <div className="reg-card">
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:"var(--navy)", marginBottom:20 }}>Create Your Account</h3>
                {error && <div className="error-box">⚠️ {error}</div>}

                <div className="form-group">
                  <label className="form-label">Full Name <span className="required">*</span></label>
                  <input className="form-input" placeholder="Dr. Chibuzo Okonkwo" value={form.name} onChange={e => set("name", e.target.value)} autoComplete="name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address <span className="required">*</span></label>
                  <input className="form-input" type="email" placeholder="you@org.org" value={form.email} onChange={e => set("email", e.target.value)} autoComplete="email" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Password <span className="required">*</span></label>
                    <input className="form-input" type="password" placeholder="At least 8 chars, 1 uppercase, 1 number" value={form.password} onChange={e => set("password", e.target.value)} autoComplete="new-password" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm Password <span className="required">*</span></label>
                    <input className="form-input" type="password" placeholder="Repeat password" value={form.confirmPassword} onChange={e => set("confirmPassword", e.target.value)} autoComplete="new-password" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input className="form-input" type="tel" placeholder="Your phone number" value={form.phone} onChange={e => set("phone", e.target.value)} autoComplete="tel" />
                </div>
                <div className="form-group">
                  <label className="form-label">Organization / NGO Name</label>
                  <input className="form-input" placeholder="Your Organization (optional)" value={form.org} onChange={e => set("org", e.target.value)} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Organization Type</label>
                    <select className="form-select" value={form.orgType} onChange={e => set("orgType", e.target.value)}>
                      <option value="">Select type...</option>
                      <option>Registered NGO</option>
                      <option>Community Group</option>
                      <option>Social Enterprise</option>
                      <option>Individual Activist</option>
                      <option>Faith-Based Organization</option>
                      <option>Government Agency</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">State of Operation <span className="required">*</span></label>
                    <select className="form-select" value={form.state} onChange={e => set("state", e.target.value)}>
                      <option value="">Select state...</option>
                      {STATES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <button className="btn-primary" style={{ width:"100%", padding:16, marginTop:4 }} onClick={handleRegister} disabled={loading}>
                  {loading
                    ? <><span className="spinner-white" />Creating Account...</>
                    : "Create Account & Proceed to Payment →"
                  }
                </button>
                <p style={{ fontSize:12, color:"var(--text-muted)", textAlign:"center", marginTop:14 }}>
                  Already a member? <Link to="/login" style={{ color:"var(--gold)" }}>Sign in here</Link>
                </p>
                <p style={{ fontSize:11, color:"var(--text-muted)", textAlign:"center", marginTop:6 }}>
                  By registering, you agree to our <Link to="/terms" style={{ color:"var(--gold)" }}>Terms & Conditions</Link>
                </p>
              </div>
            )}

            {/* ── STEP 2: KORAPAY PAYMENT ── */}
            {step === "payment" && (
              <div className="payment-card">
                <div style={{ fontSize:48, marginBottom:12 }}>💳</div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:20, color:"var(--navy)", fontWeight:700, marginBottom:4 }}>
                  Complete Your Payment
                </div>
                <div style={{ fontSize:14, color:"var(--text-muted)", marginBottom:6 }}>
                  Welcome, <strong>{payName}</strong>!
                </div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:52, fontWeight:900, color:"var(--gold)", lineHeight:1, margin:"16px 0 4px" }}>
                  ₦250
                </div>
                <div style={{ fontSize:13, color:"var(--text-muted)", marginBottom:28 }}>
                  One-time · Lifetime membership · No renewal
                </div>

                {/* KORAPAY BUTTON — uses saved uid/email/name from registration */}
                <PaymentButton
                  email={payEmail}
                  uid={payUid}
                  name={payName}
                  phone={form.phone}
                  onSuccess={handlePaymentSuccess}
                  onClose={() => {}}
                  label="Pay ₦250 — Activate Membership Now ⚡"
                />

                <div style={{ marginTop:20, padding:"14px", background:"var(--off-white)", borderRadius:4 }}>
                  <div style={{ fontSize:12, color:"var(--text-muted)", lineHeight:1.8 }}>
                    💡 <strong>Accepted:</strong> Visa, Mastercard, Verve, Bank Transfer, USSD<br />
                    🔒 Secured by <strong>Korapay</strong> — card details never stored on our servers
                  </div>
                </div>

                <p style={{ fontSize:12, color:"var(--text-muted)", marginTop:16 }}>
                  Member ID: <strong style={{ color:"var(--gold)" }}>{memberId}</strong>
                </p>
                <p style={{ fontSize:12, color:"var(--text-muted)", marginTop:8 }}>
                  Issues? Email <strong>payments@revfoundation.org</strong>
                </p>
              </div>
            )}

            {/* ── STEP 3: SUCCESS ── */}
            {step === "success" && (
              <div className="payment-card">
                <div style={{ fontSize:64, marginBottom:16 }}>🎉</div>
                <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:"var(--navy)", marginBottom:12 }}>
                  Membership Activated!
                </h3>
                <p style={{ color:"var(--text-muted)", fontSize:15, lineHeight:1.75, marginBottom:24 }}>
                  Your payment was successful. Your membership is now fully active — you can apply for grants immediately!
                </p>
                <div style={{ background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.2)", padding:"14px 18px", borderRadius:4, marginBottom:24 }}>
                  <div style={{ fontSize:13, color:"#16a34a", fontWeight:600 }}>✅ Payment Confirmed</div>
                  <div style={{ fontSize:13, color:"var(--text-muted)", marginTop:4 }}>
                    Member ID: <strong>{memberId}</strong>
                  </div>
                </div>
                <div style={{ display:"flex", gap:12, flexDirection:"column" }}>
                  <button className="btn-primary" style={{ padding:14 }} onClick={() => navigate("/apply")}>
                    Apply for a Grant Now →
                  </button>
                  <button className="btn-outline-dark" style={{ padding:14 }} onClick={() => navigate("/dashboard")}>
                    Go to My Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding:"80px 5vw", background:"var(--off-white)" }}>
        <div style={{ maxWidth:700, margin:"0 auto" }}>
          <div style={{ textAlign:"center" }}>
            <span className="section-eyebrow">Questions</span>
            <h2 className="section-title">Frequently Asked</h2>
          </div>
          <div style={{ marginTop:36 }}>
            {FAQS.map((f, i) => (
              <div className="faq-item" key={i}>
                <div className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{f.q}</span>
                  <span style={{ transition:"transform 0.3s", transform:openFaq===i?"rotate(180deg)":"none", fontSize:11, color:"var(--gold)", flexShrink:0 }}>▼</span>
                </div>
                {openFaq === i && <div className="faq-a">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}